// SidebarSecondary — 240px submenu panel. Clone từ Core's SidebarSecondary.jsx.
// Strip: TasksSecondaryNav, ApprovalsSecondaryNav (Core-specific custom navs),
//        useBadgeCounts (Core-specific). Consumer pass `badgeCounts` qua prop nếu cần.

import { NavLink, useLocation } from 'react-router-dom';
import { useRef, useEffect } from 'react';
import clsx from 'clsx';
import { tLabel } from '../../../i18n/runtime.js';
import { findActiveItem, getBreadcrumbs, filterMenuByPermissions } from '../../../navigation/menuUtils.js';
import { useAuth } from '@host/auth';

export default function SidebarSecondary({
  menu = [],
  group,
  mobile = false,
  onNavigate,
  badgeCounts = {},
  customSecondary = {},
}) {
  const location = useLocation();
  const { user } = useAuth();
  const activeItem = findActiveItem(menu, location.pathname, location.search);
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [location.pathname]);

  const breadcrumbs = getBreadcrumbs(menu, location.pathname);

  if (!group) return null;

  // Filter items by permission
  const filteredMenu = filterMenuByPermissions(menu, user?.permissions);
  const filteredGroup = filteredMenu.find((g) => g.key === group.key);
  const visibleItems = filteredGroup?.items || [];

  const Custom = customSecondary[group.key];
  const isCustom = !!Custom;

  return (
    <div className={clsx('h-full bg-white border-r border-slate-200 flex flex-col transition-all duration-300 ease-out', mobile ? 'w-[240px]' : 'w-[240px]')}>
      {!isCustom && (
        <>
          <div className="h-16 px-5 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">Navigation</p>
              <h2 className="text-base font-semibold text-slate-800 mt-0.5">{tLabel(group.label)}</h2>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-slate-100 shrink-0 bg-white">
            <div className="flex flex-wrap items-center gap-1 text-xs text-slate-400">
              {breadcrumbs.map((crumb, idx) => (
                <div key={`${tLabel(crumb.label)}-${idx}`} className="flex items-center gap-1">
                  <span className={idx === breadcrumbs.length - 1 ? 'text-slate-700 font-medium' : ''}>{tLabel(crumb.label)}</span>
                  {idx < breadcrumbs.length - 1 && <span>/</span>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {isCustom ? (
        <div className="flex-1 min-h-0 bg-white"><Custom /></div>
      ) : (
        <div className="px-3 py-4 space-y-1 overflow-y-auto flex-1 bg-white">
          {(() => {
            let lastGroup = null;
            let groupIndex = 0;
            return visibleItems.map((item) => {
              // `group` là KHOÁ gom nhóm (so sánh chuỗi), `groupLabel` là chữ ĐỂ HIỆN (có thể là
              // `` msg`…` `` cho app dịch được). Bọc i18n thẳng vào `group` là hỏng gom nhóm:
              // mỗi `msg` sinh một object riêng nên hai item cùng nhóm sẽ khác nhau, tiêu đề
              // nhóm lặp lại ở từng dòng. Chưa khai `groupLabel` thì rơi về `group` như cũ.
              const showGroupHeader = item.group && item.group !== lastGroup;
              if (showGroupHeader) groupIndex++;
              lastGroup = item.group || lastGroup;
              return (
                <div key={item.key}>
                  {showGroupHeader && (
                    <p className={clsx('text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 mb-2', groupIndex > 1 ? 'mt-5 pt-4 border-t border-slate-100' : 'mt-0')}>{tLabel(item.groupLabel) || item.group}</p>
                  )}
                  <NavLink
                    ref={activeItem?.key === item.key ? activeRef : null}
                    to={item.path}
                    end={!!item.end || item.path === group?.basePath}
                    onClick={onNavigate}
                    className={() => clsx(
                      'group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm border',
                      activeItem?.key === item.key
                        ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm'
                        : 'text-slate-600 border-transparent hover:bg-slate-50 hover:text-slate-900 hover:border-slate-200',
                    )}
                  >
                    <span className="font-medium flex items-center gap-2">
                      {tLabel(item.label)}
                      {badgeCounts[item.key] > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">
                          {badgeCounts[item.key]}
                        </span>
                      )}
                    </span>
                    <span className={clsx('w-2 h-2 rounded-full', activeItem?.key === item.key ? 'bg-blue-500' : 'bg-transparent group-hover:bg-slate-300')} />
                  </NavLink>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
